import React, { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import ListWithDeleteOption from "../../../../components/ListWithDeleteOption";
import { getExcludedUsers } from "../../../../services/groups";

type Props = {
  members: string[];
  stationIds: string[];
  excludeGroupId?: string;
  onAdd: (email: string) => void;
  onRemove: (email: string) => void;
};

const MembersInput: React.FC<Props> = ({
  members,
  stationIds,
  excludeGroupId,
  onAdd,
  onRemove,
}) => {
  const [email, setEmail] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);

  const handleAdd = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;

    if (members.includes(trimmed)) {
      setAddError("This user is already in the group.");
      return;
    }

    if (stationIds.length === 0) {
      setAddError("Select at least one station before adding members.");
      return;
    }

    setValidating(true);
    try {
      const data = await getExcludedUsers({ stationIds, excludeGroupId });
      const excluded: { email: string }[] = data?.excluded ?? [];
      if (excluded.some((u) => u.email === trimmed)) {
        setAddError("This user is already a member of another group on the selected stations.");
        return;
      }
    } finally {
      setValidating(false);
    }

    setAddError(null);
    onAdd(trimmed);
    setEmail("");
  };

  return (
    <>
      <div className="group-edit-add-row">
        <InputText
          value={email}
          onChange={(e) => { setEmail(e.target.value); setAddError(null); }}
          placeholder="Add member by email"
          className={`flex-1${addError ? " p-invalid" : ""}`}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Button
          icon="pi pi-plus"
          outlined
          onClick={handleAdd}
          disabled={!email.trim()}
          loading={validating}
          aria-label="Add member"
        />
      </div>
      {addError && (
        <small className="p-error" style={{ display: "block", marginBottom: "0.5rem" }}>
          {addError}
        </small>
      )}
      <ListWithDeleteOption
        items={members}
        getKey={(user) => user}
        renderItem={(user) => (
          <>
            <i className="pi pi-envelope" style={{ color: "#225349", fontSize: "0.85rem" }} />
            {user}
          </>
        )}
        onRemove={onRemove}
        emptyMessage="No members added."
      />
    </>
  );
};

export default MembersInput;
